import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, ImagePlus, Trash2, Check } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { photoUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Button, Field, Input, Textarea, Chip, Skeleton, Toast } from "@/ui";

const MODES = [
  { value: "product", label: "one count" },
  { value: "size", label: "per size" },
  { value: "option", label: "per option" },
];
const MODE_HINT = {
  product: "one running total for the whole item.",
  size: "each size has its own count; flavours are just choices.",
  option: "every size and flavour keeps its own count.",
};

function stockKeys(variants, mode) {
  const sizes = variants.filter((v) => v.kind === "size").map((v) => v.name).filter(Boolean);
  const all = variants.map((v) => v.name).filter(Boolean);
  if (mode === "size") return sizes.length ? sizes : [""];
  if (mode === "option") return all.length ? all : [""];
  return [""];
}

export default function ProductForm() {
  const { productId } = useParams();
  const editing = !!productId;
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [photos, setPhotos] = useState([]);
  const [variants, setVariants] = useState([]); // {id,name,kind,price}
  const [mode, setMode] = useState("product");
  const [stock, setStock] = useState({}); // key -> string/number
  const [goesWellWith, setGoesWellWith] = useState([]);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    if (!shopId) { navigate("/pick-shop", { replace: true }); return; }
    api.get(`/products?shopId=${shopId}`).then((r) => setOthers(r.data.products.filter((p) => p.id !== productId))).catch(() => {});
    if (editing) {
      api.get(`/products/${productId}?shopId=${shopId}`).then((r) => {
        const p = r.data;
        setName(p.name || ""); setDescription(p.description || ""); setCategory(p.category || "");
        setPrice(String((p.priceCentavos || 0) / 100));
        setPhotos(p.photos || []);
        setVariants((p.variants || []).map((v) => ({ id: v.id, name: v.name, kind: v.kind || "size", price: String((v.priceCentavos || 0) / 100) })));
        setMode(p.stockMode || "product");
        const sm = {}; (p.stockLines || []).forEach((l) => { sm[l.variantName] = l.onHand; });
        setStock(sm);
        setGoesWellWith(p.goesWellWith || []);
      }).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
    }
  }, [shopId, productId, editing, navigate]);

  const keys = useMemo(() => stockKeys(variants, mode), [variants, mode]);
  const sizes = variants.filter((v) => v.kind === "size");
  const flavours = variants.filter((v) => v.kind === "flavour");

  const addVariant = (kind) => setVariants((vs) => [...vs, { id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "", kind, price: "" }]);
  const setVariant = (id, field, val) => setVariants((vs) => vs.map((v) => v.id === id ? { ...v, [field]: val } : v));
  const removeVariant = (id) => setVariants((vs) => vs.filter((v) => v.id !== id));

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/photo?shopId=${shopId}`, {
        method: "POST", body: form, credentials: "include",
      });
      if (!r.ok) throw new Error("upload");
      const data = await r.json();
      setPhotos((ph) => [...ph, data.url]);
    } catch { setError("We could not add that photo. Use a JPG or PNG under 6 MB."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removePhoto = (url) => setPhotos((ph) => ph.filter((u) => u !== url));

  const toggleGoes = (id) => setGoesWellWith((g) => g.includes(id) ? g.filter((x) => x !== id) : [...g, id]);

  const save = async () => {
    setError("");
    if (!name.trim()) { setError("Give the item a name."); return; }
    if (price === "" || isNaN(Number(price))) { setError("Add a price like 150."); return; }
    setBusy(true);
    const payload = {
      shopId, name: name.trim(), description: description.trim(),
      category: category.trim() || "Uncategorised", price: Number(price),
      photos, stockMode: mode, goesWellWith,
      variants: variants.filter((v) => v.name.trim()).map((v) => ({ id: v.id.startsWith("new-") ? undefined : v.id, name: v.name.trim(), kind: v.kind, price: Number(v.price || 0) })),
      stock: Object.fromEntries(keys.map((k) => [k, Number(stock[k] || 0)])),
    };
    try {
      let res;
      if (editing) res = await api.put(`/products/${productId}`, payload);
      else res = await api.post(`/products`, payload);
      setToast("saved");
      setTimeout(() => navigate(`/products/${res.data.id}`), 500);
    } catch (e) { setError(friendlyError(e)); setBusy(false); }
  };

  if (loading) return <Layout shopName={shopName}><div className="stack"><Skeleton variant="title" /><Skeleton variant="card" /></div></Layout>;

  return (
    <Layout shopName={shopName}>
      <div className="row" style={{ marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" data-testid="form-back" onClick={() => navigate(editing ? `/products/${productId}` : "/products")}>
          <ArrowLeft size={20} strokeWidth={1.75} />back
        </button>
      </div>
      <h1 className="t-h1" style={{ marginBottom: 16 }}>{editing ? "edit item" : "new item"}</h1>
      {error ? <div className="err-text" data-testid="form-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      <div className="grid grid-2" style={{ alignItems: "start" }}>
        <div className="stack" style={{ gap: 16 }}>
          <Card>
            <div className="stack" style={{ gap: 12 }}>
              <Field label="name" htmlFor="f-name"><Input id="f-name" data-testid="f-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Adobo Rice Bowl" /></Field>
              <Field label="description" htmlFor="f-desc"><Textarea id="f-desc" data-testid="f-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="a short line customers will read" /></Field>
              <div className="grid grid-2" style={{ gap: 12 }}>
                <Field label="price (₱)" htmlFor="f-price"><Input id="f-price" data-testid="f-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="150" /></Field>
                <Field label="category" htmlFor="f-cat"><Input id="f-cat" data-testid="f-cat" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Rice Meals" /></Field>
              </div>
            </div>
          </Card>

          <Card>
            <p className="t-label muted" style={{ marginBottom: 12 }}>photos</p>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              {photos.map((u) => (
                <div key={u} style={{ position: "relative" }}>
                  <img className="thumb" src={photoUrl(u)} alt="" style={{ width: 84, height: 84 }} />
                  <button className="menu-btn" data-testid="remove-photo" onClick={() => removePhoto(u)}
                    style={{ position: "absolute", top: -6, right: -6, background: "var(--surface)", borderRadius: "50%" }}><X size={14} strokeWidth={2} /></button>
                </div>
              ))}
              <button className="counter-item" data-testid="add-photo-btn" style={{ width: 84, height: 84, alignItems: "center", justifyContent: "center" }}
                onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <span className="spinner" /> : <ImagePlus size={22} strokeWidth={1.5} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" data-testid="photo-input" style={{ display: "none" }} onChange={onPickPhoto} />
            </div>
          </Card>

          <Card>
            <VariantGroup title="sizes" kind="size" rows={sizes} onAdd={() => addVariant("size")} onChange={setVariant} onRemove={removeVariant} />
            <div className="divider" />
            <VariantGroup title="flavours" kind="flavour" rows={flavours} onAdd={() => addVariant("flavour")} onChange={setVariant} onRemove={removeVariant} />
          </Card>
        </div>

        <div className="stack" style={{ gap: 16 }}>
          <Card>
            <p className="t-label muted" style={{ marginBottom: 10 }}>how stock is counted</p>
            <div className="segmented" data-testid="stockmode" style={{ marginBottom: 8 }}>
              {MODES.map((m) => (
                <button key={m.value} className={mode === m.value ? "active" : ""} data-testid={`mode-${m.value}`} onClick={() => setMode(m.value)}>{m.label}</button>
              ))}
            </div>
            <p className="muted t-small">{MODE_HINT[mode]}</p>
          </Card>

          <Card>
            <p className="t-label muted" style={{ marginBottom: 12 }}>how many on hand</p>
            <div className="stack" data-testid="stock-editor">
              {keys.map((k) => (
                <div key={k || "_"} className="row-between">
                  <span className="t-body">{k || "whole item"}</span>
                  <Input data-testid={`stock-${k || "whole"}`} inputMode="numeric" value={stock[k] ?? 0}
                    style={{ width: 100 }} onChange={(e) => setStock((s) => ({ ...s, [k]: e.target.value }))} />
                </div>
              ))}
            </div>
          </Card>

          {others.length ? (
            <Card>
              <p className="t-label muted" style={{ marginBottom: 12 }}>goes well with</p>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }} data-testid="goes-well">
                {others.map((o) => (
                  <button key={o.id} className={`chip${goesWellWith.includes(o.id) ? " good" : ""}`} data-testid={`goes-${o.id}`}
                    onClick={() => toggleGoes(o.id)} style={{ cursor: "pointer" }}>
                    {goesWellWith.includes(o.id) ? <Check size={13} strokeWidth={2} /> : null}{o.name}
                  </button>
                ))}
              </div>
            </Card>
          ) : null}

          <Button className="btn-block" variant="primary" data-testid="save-product" loading={busy} onClick={save}>
            {editing ? "save changes" : "add item"}
          </Button>
        </div>
      </div>
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="form-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}

function VariantGroup({ title, kind, rows, onAdd, onChange, onRemove }) {
  return (
    <div>
      <div className="row-between" style={{ marginBottom: 10 }}>
        <p className="t-label muted">{title}</p>
        <Button size="sm" data-testid={`add-${kind}`} onClick={onAdd}><Plus size={14} strokeWidth={1.75} />add {kind}</Button>
      </div>
      {rows.length === 0 ? <p className="muted-3 t-small">none yet</p> : (
        <div className="stack" style={{ gap: 8 }}>
          {rows.map((v) => (
            <div key={v.id} className="row" style={{ gap: 8 }}>
              <Input data-testid={`${kind}-name`} value={v.name} placeholder={kind === "size" ? "Large" : "Ube"} onChange={(e) => onChange(v.id, "name", e.target.value)} style={{ flex: 1 }} />
              <Input data-testid={`${kind}-price`} inputMode="decimal" value={v.price} placeholder="₱" onChange={(e) => onChange(v.id, "price", e.target.value)} style={{ width: 90 }} />
              <button className="menu-btn" aria-label="remove" data-testid={`${kind}-remove`} onClick={() => onRemove(v.id)}><Trash2 size={16} strokeWidth={1.75} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
